import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  Container,
} from '@mui/material';
import { Menu as MenuIcon, Dashboard, Logout } from '@mui/icons-material';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

import logo from '../../assets/logo.jpeg';

const MainLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  /* ================= SCROLL EFFECT ================= */
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
    handleMenuClose();
  };

  const navItems = [
    { text: 'Home', path: '/' },
    { text: 'Fruits', path: '/fruits' },
    { text: 'How It Works', path: '/how-it-works' },
    { text: 'About Us', path: '/about' },
    { text: 'Contact', path: '/contact' },
  ];

  /* ================= DRAWER ================= */
  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ width: 260 }}>
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Box component="img" src={logo} alt="SmartWave" sx={{ height: 42 }} />
      </Box>

      <List>
        {navItems.map((item) => (
          <ListItem
            key={item.text}
            button
            component={Link}
            to={item.path}
            sx={{
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(25,118,210,0.08)',
                transform: 'translateX(6px)',
              },
            }}
          >
            <ListItemText primary={item.text} />
          </ListItem>
        ))}

        {!user ? (
          <>
            <ListItem button component={Link} to="/login">
              <ListItemText primary="Login" />
            </ListItem>
            <ListItem button component={Link} to="/register">
              <ListItemText primary="Register" />
            </ListItem>
          </>
        ) : (
          <>
            <ListItem
              button
              component={Link}
              to={
                user.user_type === 'admin'
                  ? '/admin/dashboard'
                  : '/buyer/dashboard'
              }
            >
              <ListItemText primary="Dashboard" />
            </ListItem>
            <ListItem button onClick={handleLogout}>
              <ListItemText primary="Logout" />
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* ================= HEADER ================= */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          backgroundColor: scrolled
            ? 'rgba(255,255,255,0.95)'
            : 'rgba(255,255,255,0.85)',
          borderBottom: scrolled ? '1px solid rgba(0,0,0,0.08)' : 'none',
          boxShadow: scrolled
            ? '0 8px 30px rgba(0,0,0,0.08)'
            : 'none',
          color: scrolled ? '#1976d2' : '#000',
          transition: 'all 0.35s ease',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {isMobile && (
              <IconButton sx={{ color: 'inherit' }} onClick={handleDrawerToggle}>
                <MenuIcon />
              </IconButton>
            )}

            {/* LOGO */}
            <Box component={Link} to="/" sx={{ flexGrow: 1 }}>
              <Box component="img" src={logo} alt="SmartWave" sx={{ height: 42 }} />
            </Box>

            {/* DESKTOP MENU */}
            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {navItems.map((item) => (
                  <Button
                    key={item.text}
                    component={Link}
                    to={item.path}
                    sx={{
                      color: 'inherit',
                      fontWeight: 500,
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: '50%',
                        bottom: 0,
                        width: 0,
                        height: '2px',
                        backgroundColor: 'primary.main',
                        transition: 'all 0.3s ease',
                        transform: 'translateX(-50%)',
                      },
                      '&:hover': {
                        color: 'primary.main',
                      },
                      '&:hover::after': {
                        width: '70%',
                      },
                    }}
                  >
                    {item.text}
                  </Button>
                ))}

                {!user ? (
                  <>
                    <Button component={Link} to="/login" variant="outlined">
                      Login
                    </Button>
                    <Button component={Link} to="/register" variant="contained">
                      Register
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      component={Link}
                      to={
                        user.user_type === 'admin'
                          ? '/admin/dashboard'
                          : '/buyer/dashboard'
                      }
                      startIcon={<Dashboard />}
                      sx={{ color: 'inherit' }}
                    >
                      Dashboard
                    </Button>
                    <IconButton onClick={handleMenuOpen}>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {user.email?.charAt(0).toUpperCase()}
                      </Avatar>
                    </IconButton>
                  </>
                )}
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* MOBILE DRAWER */}
      <Drawer open={mobileOpen} onClose={handleDrawerToggle}>
        {drawer}
      </Drawer>

      {/* USER MENU */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleLogout}>
          <Logout fontSize="small" sx={{ mr: 1 }} /> Logout
        </MenuItem>
      </Menu>

      {/* HEADER SPACER */}
      <Toolbar />

      {/* ================= CONTENT ================= */}
      <Box component="main" sx={{ flex: 1 }}>
        <Outlet />
      </Box>

      {/* ================= FOOTER ================= */}
      <Box sx={{ py: 3, backgroundColor: '#f5f5f5' }}>
        <Container maxWidth="xl">
          <Typography align="center" variant="body2">
            © {new Date().getFullYear()} SmartWave. All rights reserved.
          </Typography>

          <Typography
            align="center"
            variant="caption"
            sx={{
              mt: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            Built with ❤️{' '}
            <Box
              component="a"
              href="https://seo9ine.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'primary.main',
                fontWeight: 700,
                textDecoration: 'none',
                position: 'relative',
                transition: 'all 0.3s ease',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  bottom: -2,
                  width: '100%',
                  height: '2px',
                  backgroundColor: 'primary.main',
                  transform: 'scaleX(0)',
                  transformOrigin: 'right',
                  transition: 'transform 0.3s ease',
                },
                '&:hover': {
                  letterSpacing: '1px',
                },
                '&:hover::after': {
                  transform: 'scaleX(1)',
                  transformOrigin: 'left',
                },
              }}
            >
              seo9ine
            </Box>
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default MainLayout;
